import { useState, useCallback, useEffect, useRef } from 'react';
import { formatPrice } from '../../utils/formatPrice';
import { DiscountInfoModal } from './DiscountInfoModal';
import { DiscountPreviewModal } from './DiscountPreviewModal';
import type { DiscountInfo } from '../../types/cart';

interface DiscountPreviewData {
  code: string;
  discount: number;
  discountPercentage: number;
  discountableItems: string[];
  discountInfo: DiscountInfo;
}

interface DiscountCodeInputProps {
  initialCode: string | null;
  discountAmount: number;
  discountError: string | null;
  discountInfo: DiscountInfo | null;
  isLoading: boolean;
  onApplyDiscount: (code: string) => Promise<void>;
  onPreviewDiscount: (code: string) => Promise<void>;
  onConfirmDiscount: () => Promise<void>;
  onCancelPreview: () => void;
  previewData: DiscountPreviewData | null;
  onClearDiscount: () => Promise<void>;
}

export function DiscountCodeInput({
  initialCode,
  discountAmount,
  discountError,
  discountInfo,
  isLoading,
  onApplyDiscount,
  onPreviewDiscount,
  onConfirmDiscount,
  onCancelPreview,
  previewData,
  onClearDiscount,
}: DiscountCodeInputProps) {
  const [code, setCode] = useState(initialCode || '');
  // Initialize as applied if we have a code and discountInfo (means it's in session)
  // Even if discountAmount is 0 (e.g., address doesn't match neighborhood restriction)
  const [isApplied, setIsApplied] = useState(() => !!initialCode && !!discountInfo);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const initialApplyDone = useRef(false);

  // Auto-apply initial code on mount ONLY if code exists but no discountInfo
  // (e.g., from /d/CODE redirect where we need to call the API to get info)
  useEffect(() => {
    if (initialCode && !initialApplyDone.current && !discountInfo && !discountError) {
      initialApplyDone.current = true;
      onApplyDiscount(initialCode);
    }
  }, [initialCode, discountInfo, discountError, onApplyDiscount]);

  // When discount is successfully applied (has info), mark as applied
  useEffect(() => {
    if (discountInfo && !discountError) {
      setIsApplied(true);
    }
  }, [discountInfo, discountError]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || isLoading) return;
    // First step: preview the discount
    await onPreviewDiscount(code.trim());
  }, [code, isLoading, onPreviewDiscount]);

  const handleConfirm = useCallback(async () => {
    await onConfirmDiscount();
  }, [onConfirmDiscount]);

  const handleClear = useCallback(async () => {
    await onClearDiscount();
    setCode('');
    setIsApplied(false);
  }, [onClearDiscount]);

  // Show applied state when we have a code with discountInfo (means it's in session)
  if (isApplied) {
    return (
      <div className="mb-4">
        <label className="block text-[13px] text-gray-500 mb-1.5">Código de descuento</label>
        <div className="py-2 px-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-green-700 text-sm font-medium min-w-0">
              <svg className="shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className="uppercase truncate">{discountInfo?.display_name || code}</span>
              {discountAmount > 0 && (
                <span className="text-green-600 font-normal shrink-0">(-{formatPrice(discountAmount)})</span>
              )}
            </div>
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className="flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors disabled:opacity-50 shrink-0"
              title="Eliminar cupón"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {discountInfo && (
            <button
              type="button"
              onClick={() => setShowInfoModal(true)}
              className="text-xs text-green-600 hover:text-green-700 underline underline-offset-2 mt-1 ml-6"
            >
              Ver condiciones
            </button>
          )}
        </div>

        {showInfoModal && discountInfo && (
          <DiscountInfoModal
            discountInfo={discountInfo}
            discountCode={code}
            onClose={() => setShowInfoModal(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <form onSubmit={handleSubmit}>
        <label className="block text-[13px] text-gray-500 mb-1.5">Código de descuento</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              className={`w-full py-2.5 px-3 border rounded-lg text-sm transition-colors duration-150 focus:outline-none ${
                discountError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-green-500'
              }`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Ingresá tu código"
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !code.trim()}
            className="px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isLoading ? (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Verificar'
            )}
          </button>
        </div>
      </form>

      {code && !isLoading && discountError && (
        <div className="flex items-start gap-1.5 mt-2 text-[13px] text-red-500">
          <svg className="shrink-0 mt-0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {discountError}
        </div>
      )}

      {previewData && (
        <DiscountPreviewModal
          discountCode={previewData.code}
          discountAmount={previewData.discount}
          discountInfo={previewData.discountInfo}
          isLoading={isLoading}
          onConfirm={handleConfirm}
          onCancel={onCancelPreview}
        />
      )}
    </div>
  );
}
